import { api } from '../api.js';
import {
  h, money, moneyCents, compact, isDebt, getRange, rangeLong, rangeSwitch, lineChart, accountChange,
  openSheet, backButton, shortDate, monthName, section, pair, monthBars, type Range, type SeriesPoint,
} from '../ui.js';

// Account: balance over time, what this account spent, its transactions, and
// its settings (rename, in net or not, archive; manual accounts update by hand).

interface Txn {
  id: number; date: string; description: string; amount: number;
  not_spending: number; always: number; transfer: number;
  payee_kind: string | null; balance_transfer: number; subscription: number;
}
interface BigPurchase { id: number; date: string; description: string; amount: number }
interface Detail {
  range: Range;
  account: {
    id: number; name: string; type: string; institution: string; manual: boolean;
    in_net: boolean; archived: boolean; balance: number; change: number; series: SeriesPoint[];
    promo_end: string | null;
  };
  spending: {
    this_month: number; last_month: number; last_month_same_point: number; same_point_date: string;
    months: { month: string; total: number }[]; big_purchases: BigPurchase[];
  } | null;
  debt: {
    owed: number; pace_per_month: number; clears: string | null; interest_this_month: number;
    promo: { ends: string; on_track: boolean; needed_per_month: number } | null;
  } | null;
  market: { from: string; start: number; added: number; market: number; market_pct: number; end: number } | null;
  transactions: Txn[];
  more: boolean;
}

export function accountScreen(): HTMLElement {
  const id = parseInt(new URLSearchParams(location.hash.split('?')[1] ?? '').get('id') ?? '', 10);
  const root = h('div', 'screen');
  const top = h('div', 'topbar');
  top.appendChild(backButton());
  const body = h('div');
  body.appendChild(h('div', 'loading', 'Loading…'));
  root.append(top, body);

  let range = getRange();
  let query = '';

  async function load(): Promise<void> {
    try {
      const d = await api.get<Detail>(`/accounts/${id}/detail?range=${range}`);
      render(d);
    } catch (err) {
      if (err instanceof Error && err.message === 'Unauthorized') return;
      body.innerHTML = '';
      body.appendChild(h('div', 'error', err instanceof Error ? err.message : 'Could not load'));
    }
  }

  function render(d: Detail): void {
    const a = d.account;
    const debt = isDebt(a.type);
    body.innerHTML = '';
    body.append(h('div', 'title', a.name), h('div', 'sub', a.manual ? 'Manual account' : a.institution));
    if (a.archived) body.appendChild(h('div', 'note', 'Archived: hidden from Home and net.'));

    // ── Balance ──
    const bal = section(debt ? 'Owed' : 'Balance');
    bal.body.appendChild(h('div', 'money hero', money(debt ? -a.balance : a.balance)));
    const chg = accountChange(a.type, a.change);
    chg.className = chg.className.replace('acct-chg', 'change');
    chg.appendChild(h('span', 'muted', ' ' + rangeLong(range)));
    bal.body.append(
      chg,
      lineChart(a.series, { height: 200, axes: true, long: range === 'all' || range === '1y' }),
      rangeSwitch(range, (r) => { range = r; void load(); }),
    );
    if (d.market) bal.body.appendChild(marketBlock(d.market));
    if (d.debt) bal.body.appendChild(debtBlock(d.debt, a.promo_end));
    body.appendChild(bal.el);

    // ── Spending ──
    if (d.spending) {
      const sp = section('Spending');
      sp.body.appendChild(pair([
        { label: 'Spent so far', value: money(d.spending.this_month) },
        { label: `By ${shortDate(d.spending.same_point_date)}`, value: money(d.spending.last_month_same_point) },
      ]));
      if (d.spending.months.length > 1) {
        const caption = h('div', 'note', 'Tap a month.');
        sp.body.append(monthBars(d.spending.months, null, (m) => { caption.textContent = `${monthName(m.month)}: ${money(m.total)}`; }), caption);
      }
      if (d.spending.big_purchases.length) {
        sp.body.appendChild(h('div', 'label sub-head', 'Big purchases'));
        for (const b of d.spending.big_purchases) {
          const row = h('div', 'txn');
          const desc = h('div', 'txn-desc');
          desc.appendChild(h('span', '', b.description));
          row.append(h('div', 'txn-date', shortDate(b.date)), desc, h('div', 'txn-amt', moneyCents(b.amount)));
          sp.body.appendChild(row);
        }
      }
      body.appendChild(sp.el);
    }

    // ── Transactions ── (a brokerage Plaid reports no transactions for gets none)
    if (a.type === 'investment' && d.transactions.length === 0 && !query) {
      body.appendChild(settingsSection(d));
      return;
    }
    const tx = section('Transactions');
    const search = h('input', 'search');
    search.type = 'search';
    search.placeholder = 'Search';
    search.value = query;
    const list = h('div');
    let timer: ReturnType<typeof setTimeout> | undefined;
    search.addEventListener('input', () => {
      clearTimeout(timer);
      timer = setTimeout(() => { query = search.value.trim(); void loadTxns(list, true); }, 250);
    });
    tx.body.append(search, list);
    fillTxns(list, d.transactions, d.more, true);
    body.appendChild(tx.el);

    body.appendChild(settingsSection(d));
  }

  let offset = 0;
  async function loadTxns(list: HTMLElement, reset: boolean): Promise<void> {
    if (reset) offset = 0;
    const q = query ? `&q=${encodeURIComponent(query)}` : '';
    const d = await api.get<Detail>(`/accounts/${id}/detail?range=${range}&offset=${offset}${q}`);
    fillTxns(list, d.transactions, d.more, reset);
  }

  function fillTxns(list: HTMLElement, txns: Txn[], more: boolean, reset: boolean): void {
    if (reset) { list.innerHTML = ''; offset = 0; }
    list.querySelector('.more')?.remove();
    offset += txns.length;
    for (const t of txns) list.appendChild(txnRow(t));
    if (offset === 0) list.appendChild(h('div', 'empty', query ? 'Nothing matches.' : 'No transactions.'));
    if (more) {
      const btn = h('button', 'link more', 'Show more');
      btn.addEventListener('click', () => { btn.disabled = true; void loadTxns(list, false); });
      list.appendChild(btn);
    }
  }

  function txnRow(t: Txn): HTMLElement {
    const outflow = t.amount > 0;
    const excluded = !!t.transfer || !!t.not_spending || !!t.always || !!t.balance_transfer;
    const row = h('button', outflow && excluded ? 'txn dim' : 'txn');
    const desc = h('div', 'txn-desc');
    desc.appendChild(h('span', '', t.description));
    if (t.transfer) desc.appendChild(h('span', 'tag', 'transfer'));
    else if (t.balance_transfer) desc.appendChild(h('span', 'tag', 'balance transfer'));
    else if (t.payee_kind === 'invest') desc.appendChild(h('span', 'tag', 'investing'));
    else if (outflow && excluded) desc.appendChild(h('span', 'tag', 'not spending'));
    else if (t.subscription) desc.appendChild(h('span', 'tag', 'subscription'));
    const amt = h('div', outflow ? 'txn-amt' : 'txn-amt up', (outflow ? '' : '+') + moneyCents(t.amount));
    row.append(h('div', 'txn-date', shortDate(t.date)), desc, amt);
    // Only money leaving can be spending; transfers are already decided.
    if (outflow && !t.transfer && !t.balance_transfer) row.addEventListener('click', () => spendingSheet(t));
    else row.style.cursor = 'default';
    return row;
  }

  function spendingSheet(t: Txn): void {
    openSheet((sheet, close) => {
      sheet.append(h('h3', '', t.description), h('div', 'sub', `${shortDate(t.date)} · ${moneyCents(t.amount)}`));
      const counts = checkbox('Counts as spending', !(t.not_spending || t.always));
      const always = checkbox(`Never count "${t.description}"`, !!t.always);
      const sub = checkbox(`Track "${t.description}" as a subscription`, !!t.subscription);
      always.input.disabled = counts.input.checked;
      counts.input.addEventListener('change', () => {
        always.input.disabled = counts.input.checked;
        if (counts.input.checked) always.input.checked = false;
      });
      const save = h('button', 'btn primary', 'Save');
      save.style.width = '100%';
      save.style.marginTop = '12px';
      save.addEventListener('click', async () => {
        save.disabled = true;
        try {
          await api.post(`/transactions/${t.id}/not-spending`, { value: !counts.input.checked, always: always.input.checked });
          if (sub.input.checked !== !!t.subscription) {
            await api.post('/subscriptions', { description: t.description, value: sub.input.checked });
          }
          close();
          void load();
        } catch (err) {
          save.disabled = false;
          alert(err instanceof Error ? err.message : 'Could not save');
        }
      });
      const note = h('div', 'row-sub', 'Use this for money that leaves but isn’t spent: a loan payment, a deposit to an account the bank doesn’t report.');
      note.style.marginTop = '6px';
      sheet.append(counts.label, always.label, note, sub.label, save);
    });
  }

  /** A brokerage: what was there, what you put in, and what the market did. */
  function marketBlock(m: NonNullable<Detail['market']>): HTMLElement {
    const wrap = h('div');
    const split = h('div', 'split');
    const cell = (label: string, value: string, cls = '') => {
      const c = h('div');
      c.append(h('div', 'label', label), h('div', `money ${cls}`, value));
      return c;
    };
    split.append(
      cell(`On ${shortDate(m.from)}`, money(m.start)),
      cell('You added', money(m.added)),
      cell('Market', `${m.market >= 0 ? '+' : '−'}${money(Math.abs(m.market))}`, m.market >= 0 ? 'up' : 'down'),
    );
    wrap.append(split, h('div', 'note', `Market ${m.market_pct >= 0 ? '+' : ''}${m.market_pct}% on what was there plus what you added.`));
    return wrap;
  }

  /** A card or loan: interest, pace, payoff month, and the 0% deadline. */
  function debtBlock(debt: NonNullable<Detail['debt']>, promoEnd: string | null): HTMLElement {
    const wrap = h('div');
    wrap.style.marginTop = '10px';
    if (debt.interest_this_month > 0) wrap.appendChild(h('div', 'note', `Interest this month ${moneyCents(debt.interest_this_month)}.`));
    wrap.appendChild(h('div', 'note', debt.pace_per_month > 0
      ? `Paying it down ${compact(debt.pace_per_month)} a month (last 3 months)${debt.clears ? `: clear by ${monthName(debt.clears)}` : ''}.`
      : 'Not going down over the last 3 months.'));
    if (debt.promo && !debt.promo.on_track) {
      const warn = h('div', 'banner');
      warn.style.marginTop = '10px';
      warn.appendChild(h('span', '', `0% ends ${shortDate(debt.promo.ends)} ${debt.promo.ends.slice(0, 4)}. At this pace it won't be paid off; about ${money(debt.promo.needed_per_month)} a month would do it.`));
      wrap.appendChild(warn);
    } else if (debt.promo) {
      wrap.appendChild(h('div', 'note up', `On track to clear before 0% ends ${shortDate(debt.promo.ends)} ${debt.promo.ends.slice(0, 4)}.`));
    }
    const set = h('button', 'link', promoEnd ? `0% until ${shortDate(promoEnd)} ${promoEnd.slice(0, 4)} · change` : 'Set a 0% end date');
    set.addEventListener('click', async () => {
      const raw = prompt('0% APR ends on (YYYY-MM-DD). Leave empty to clear.', promoEnd ?? '');
      if (raw == null) return;
      const ends = raw.trim() || null;
      if (ends && !/^\d{4}-\d{2}-\d{2}$/.test(ends)) { alert('Use YYYY-MM-DD, e.g. 2027-12-19'); return; }
      await api.post(`/accounts/${id}/promo`, { ends });
      void load();
    });
    wrap.appendChild(set);
    return wrap;
  }

  function settingsSection(d: Detail): HTMLElement {
    const a = d.account;
    const sec = section('Account');
    const wrap = sec.el;
    const actions = h('div', 'actions');

    const rename = h('button', 'btn small', 'Rename');
    rename.addEventListener('click', async () => {
      const name = prompt('Account name', a.name)?.trim();
      if (!name || name === a.name) return;
      await api.patch(`/accounts/${a.id}`, { name });
      void load();
    });

    const net = h('button', 'btn small', a.in_net ? 'Leave out of net' : 'Include in net');
    net.addEventListener('click', async () => {
      await api.patch(`/accounts/${a.id}`, { exclude_from_net_worth: a.in_net ? 1 : 0 });
      void load();
    });

    const archive = h('button', 'btn small', a.archived ? 'Unarchive' : 'Archive');
    archive.addEventListener('click', async () => {
      if (a.archived) {
        await api.patch(`/accounts/${a.id}`, { is_active: 1 });
        void load();
        return;
      }
      if (!confirm(`Archive ${a.name}? It leaves Home and net. Its history stays.`)) return;
      await api.patch(`/accounts/${a.id}`, { is_active: 0 });
      location.hash = '#home';
    });
    actions.append(rename, net, archive);

    if (a.manual) {
      const update = h('button', 'btn small primary', 'Update balance');
      update.addEventListener('click', async () => {
        const debt = isDebt(a.type);
        const raw = prompt(debt ? 'Amount owed today' : 'Balance today', String(Math.abs(Math.round(a.balance * 100) / 100)));
        if (raw == null) return;
        const n = parseFloat(raw.replace(/[$,\s]/g, ''));
        if (!isFinite(n)) { alert('Enter a number'); return; }
        await api.post('/balances/snapshot', { account_id: a.id, balance: debt ? -Math.abs(n) : n });
        void load();
      });
      const del = h('button', 'btn small danger', 'Delete');
      del.addEventListener('click', async () => {
        if (!confirm(`Delete ${a.name} and its balance history? This can’t be undone.`)) return;
        await api.del(`/accounts/${a.id}`);
        location.hash = '#home';
      });
      actions.prepend(update);
      actions.append(del);
    }
    sec.body.appendChild(actions);
    return wrap;
  }

  void load();
  return root;
}

function checkbox(text: string, checked: boolean): { label: HTMLLabelElement; input: HTMLInputElement } {
  const label = h('label', 'check');
  const input = h('input');
  input.type = 'checkbox';
  input.checked = checked;
  label.append(input, h('span', '', text));
  return { label, input };
}
